import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useInterpret } from '@xstate/react';
import useLazyRef from 'src/hooks/useLazyRef';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useCurrentPage, useScreenNav } from 'src/components/DNA';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import useIsTenantAdmin from 'src/hooks/useIsTenantAdmin'
import { DNARoute, ROUTES } from 'src/router/routes';
import folderQuery from 'src/state/redux/folder/query';
import { useAllFoldersUpdatedDocsMap } from 'src/state/redux/selector/folder';
import { useSearchTextContextProvider } from 'src/components/Header/SearchInput/SearchInput';
import navigationMenuMachine from './navigationMenu.machine';
import { AllNavigationItemsConfig, NavigationMenuService } from './navigationMenu.types';
import { createRouteChangeObservable } from '../observables';

export interface NavigationMenuContextType {
  service: NavigationMenuService,
  toggleCollapseState: () => void,
  navigateTo: (route: DNARoute) => void,
}

export interface ProviderProps {
  children: ReactNode
}

export const NavigationMenuContext = createContext<NavigationMenuContextType>(null!);
NavigationMenuContext.displayName = 'NavigationMenuContext';

const NavigationMenuStateProvider: React.FC<PropsWithChildren<ProviderProps>> = ({
  children,
}) => {
  const { deviceMode } = useAppSettings();
  const isTenantAdmin = useIsTenantAdmin();
  const currentPage = useCurrentPage();
  const { go, history } = useScreenNav();
  const { setCurrentSearchString } = useSearchTextContextProvider();
  const featureFlags = useFeatureFlags(
    'disablePublisherFolder',
    'enablePublisherReports',
    'enableEmailTemplates',
    'enableMSLReports',
    'enableHubs',
    'BEAC_6094_hubs_templates',
    'beac_7469_publisher_run_integration',
    'enableMyUploads',
    'BEAC_7570_compliance_checker',
    'enableMeetingFeatures',
    'enableFolderManagement',
    'enableHomeFavoriteFiles',
    'enableHomeFavoriteFolders',
  );

  const currentActiveFilter = useMemo(() => folderQuery.merge(folderQuery.filters.active), []);
  const updatedDocumentsMap = useAllFoldersUpdatedDocsMap(currentActiveFilter);

  const myUpdatesItemsCount = useMemo(() => {
    return Array.from(updatedDocumentsMap.values()).reduce((sum, items) => sum + items.length, 0);
  }, [updatedDocumentsMap]);

  // Check if user is a publisher (non-tenant admin publisher)
  const canAccessAdminSettings = isTenantAdmin || featureFlags.beac_7469_publisher_run_integration;

  const ALL_NAVIGATION_ITEMS_CONFIG: AllNavigationItemsConfig = useMemo(() => ({
    viewer: [
      { route: ROUTES.UI2_DASHBOARD, condition: true },
      { route: ROUTES.UI2_LIBRARY, condition: true },
      {
        route: ROUTES.UI2_FOLDERS,
        condition: featureFlags.enableFolderManagement,
        children: [
          { route: ROUTES.UI2_FOLDERS_MY_FOLDERS, condition: true },
          { route: ROUTES.UI2_FOLDERS_SHARED_WITH_ME, condition: true },
          { route: ROUTES.UI2_FOLDERS_MY_UPLOADS, condition: featureFlags.enableMyUploads },
          { route: ROUTES.UI2_FOLDERS_MY_UPDATES, condition: true, count: myUpdatesItemsCount },
          { route: ROUTES.UI2_FOLDERS_ARCHIVED, condition: true },
        ],
      },
      {
        route: ROUTES.UI2_FAVORITES_PARENT,
        condition: featureFlags.enableHomeFavoriteFiles || featureFlags.enableHomeFavoriteFolders,
        children: featureFlags.enableHomeFavoriteFolders ? [
          { route: ROUTES.UI2_FAVORITE_FILES, condition: true },
          { route: ROUTES.UI2_FAVORITE_FOLDERS, condition: true },
        ] : undefined,
      },
      { route: ROUTES.UI2_MEETING_HISTORY, condition: featureFlags.enableMeetingFeatures },
      { route: ROUTES.UI2_HUBS, condition: featureFlags.enableHubs },
      { route: ROUTES.UI2_ANALYTICS, condition: featureFlags.enableMSLReports },
    ],
    publisher: [
      { route: ROUTES.UI2_PUBLISHER_LIBRARY, condition: true },
      {
        route: ROUTES.UI2_PUBLISHER_FOLDERS,
        condition: !featureFlags.disablePublisherFolder && featureFlags.enableFolderManagement,
        children: [
          { route: ROUTES.UI2_PUBLISHER_FOLDERS_MY_FOLDERS, condition: true },
          { route: ROUTES.UI2_PUBLISHER_FOLDERS_SHARED_WITH_ME, condition: true },
          { route: ROUTES.UI2_PUBLISHER_FOLDERS_MY_UPDATES, condition: true, count: myUpdatesItemsCount },
          { route: ROUTES.UI2_PUBLISHER_FOLDERS_ARCHIVED, condition: true },
        ],
      },
      { route: ROUTES.UI2_PUBLISHER_EMAIL_TEMPLATES, condition: featureFlags.enableEmailTemplates },
      { route: ROUTES.UI2_PUBLISHER_HUB_TEMPLATES, condition: featureFlags.BEAC_6094_hubs_templates },
      { route: ROUTES.UI2_PUBLISHER_ANALYTICS, condition: featureFlags.enablePublisherReports },
      {
        route: ROUTES.UI2_PUBLISHER_ADMIN,
        condition: canAccessAdminSettings,
        children: [
          { route: ROUTES.UI2_PUBLISHER_ADMIN_USER, condition: isTenantAdmin },
          { route: ROUTES.UI2_PUBLISHER_AI_BEACON, condition: featureFlags.BEAC_7570_compliance_checker },
          { route: ROUTES.UI2_PUBLISHER_ADMIN_INTEGRATION, condition: canAccessAdminSettings },
        ],
      },
    ],
  }), [featureFlags, isTenantAdmin, canAccessAdminSettings, myUpdatesItemsCount])

  /* STATE MACHINE START */
  const navigationMenuMachineInstance = useLazyRef(() => navigationMenuMachine.withContext({
    deviceMode,
    collapsed: true,
    allNavigationItemsConfig: ALL_NAVIGATION_ITEMS_CONFIG,
    currentRoute: currentPage,
  }))

  const service = useInterpret(
    navigationMenuMachineInstance.current!,
    // { devTools: true },
  )

  // Subscribe to route changes and send to the machine
  useEffect(() => {
    const subscription = createRouteChangeObservable(history).subscribe(event => {
      service.send(event as any);
    });
    return () => subscription.unsubscribe();
  }, [service, history]);

  useEffect(() => {
    service.send({ type: 'UPDATE_ALL_NAVIGATION_ITEMS_CONFIG', payload: ALL_NAVIGATION_ITEMS_CONFIG });
  }, [ALL_NAVIGATION_ITEMS_CONFIG]);

  /* STATE MACHINE END */

  const toggleCollapseState = useCallback(() => {
    service.send({ type: 'TOGGLE_COLLAPSE_STATE' });
  }, [service]);

  const navigateTo = useCallback((route: DNARoute) => {
    go(route);
    setCurrentSearchString('')
  }, [go, setCurrentSearchString]);

  const context: NavigationMenuContextType = useMemo(() => ({
    service,
    toggleCollapseState,
    navigateTo,
  }), [service, toggleCollapseState, navigateTo]);

  return (
    <NavigationMenuContext.Provider value={context}>
      {children}
    </NavigationMenuContext.Provider>
  );
};

NavigationMenuStateProvider.displayName = 'NavigationMenuStateProvider';

export const useNavigationMenuState = () => {
  const context = useContext(NavigationMenuContext);
  if (!context) {
    throw new Error('useNavigationMenuState must be used within the NavigationMenuStateProvider');
  }
  return context;
};

export default NavigationMenuStateProvider;
